import { z } from 'zod';
import { ItemKey } from './config.js';
import { BundleContext } from './publishing.js';
/**
 * The answer engine's shared definitions. Every channel (form, chat, document extraction, integration,
 * admin, system) records answers through the same `responses` API, with the same checks.
 */
export const CHANNELS = ['form', 'chat', 'admin', 'document', 'integration', 'system'];
export const Channel = z.enum(CHANNELS);
/** Channels a signed-in person can use. Document, integration and system answers come from the core's own
 * pipelines, never from a browser. */
export const USER_CHANNELS = ['form', 'chat', 'admin'];
/** The provenance each channel records. Set by the core, never taken from the client. */
export const CHANNEL_PROVENANCE = {
    form: ['USER_DECLARED'],
    // The user confirmed what the bot understood, so it is still their declaration (architecture doc).
    chat: ['USER_DECLARED'],
    admin: ['ADMIN_ENTERED'],
    document: ['AI_EXTRACTED', 'DOCUMENT_VERIFIED'],
    integration: ['API_DISCOVERED', 'EXTERNAL_PROVIDER', 'SECURITY_SCAN'],
    system: ['INFERRED'],
};
export const AnswerEvidence = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('chat_message'),
        turn_id: z.string().min(1).max(100),
        /** The user's own words that support the answer. */
        quote: z.string().max(1000).optional(),
    }),
    z.strictObject({
        kind: z.literal('document'),
        document_id: z.string().min(1).max(100),
        page: z.number().int().positive().optional(),
        quote: z.string().max(1000).optional(),
    }),
    z.strictObject({
        kind: z.literal('integration'),
        connector: z.string().min(1).max(100),
        ref: z.string().max(500).optional(),
    }),
    z.strictObject({ kind: z.literal('note'), text: z.string().min(1).max(1000) }),
]);
export const AnswerInput = z.strictObject({
    question: z.string().min(1).max(64),
    /** null clears the answer. */
    value: z.unknown(),
    /**
     * The response this answer replaces, as the client last saw it (null: the question had no answer).
     * When given and someone answered in between, the whole submission is refused with 409.
     */
    based_on: z.string().uuid().nullable().optional(),
    /**
     * Chat only. "card": confirmed together with other answers on one card. "button": this answer was
     * confirmed on its own. Mandatory and kill-switch questions need "button".
     */
    confirmation: z.enum(['card', 'button']).optional(),
    evidence: z.array(AnswerEvidence).max(10).default([]),
    confidence: z.number().min(0).max(1).optional(),
});
export const SubmitResponses = z.strictObject({
    channel: z.enum(USER_CHANNELS),
    answers: z.array(AnswerInput).min(1).max(50),
});
export const CreateAssessment = z.strictObject({
    title: z.string().trim().min(1).max(200),
    /** The business being assessed, as the tenant identifies it. Also the customer scope key. */
    customer: z.string().trim().min(1).max(100),
    customer_name: z.string().trim().min(1).max(200),
    country: BundleContext.shape.country.unwrap().unwrap(),
    industry: z.string().trim().min(1).max(100),
    product: z.string().trim().min(1).max(100).optional(),
    /**
     * The journey to follow, by key. Optional when the configuration for this context has exactly one;
     * without any, the assessment is a plain set of questions.
     */
    journey: ItemKey.optional(),
    /**
     * One IT environment of a group (task 94, decision 0071): its name, the group's companies it covers (the
     * client or its subsidiaries, by key), and another assessment of the client to copy the company's facts
     * from, as answers the client confirms.
     */
    environment: z
        .strictObject({
        name: z.string().trim().min(1).max(120),
        entities: z.array(z.string().trim().min(1).max(200)).max(50).default([]),
        copy_from: z.string().uuid().optional(),
    })
        .optional(),
});
/**
 * The effects an answer triggers: the chosen options' effects for choice questions, the question's own
 * effects for value answers. They are recorded with the answer and executed by the rules engine.
 */
export function answerEffects(q, value) {
    if (value === null || value === undefined)
        return [];
    if (q.options.length) {
        const picked = new Set((Array.isArray(value) ? value : [value]).map(String));
        return q.options.filter((o) => picked.has(o.value)).flatMap((o) => o.effects);
    }
    return q.effects;
}
const money = (amount, currency) => `${currency === 'MYR' ? 'RM' : currency} ${amount.toLocaleString('en-MY', { maximumFractionDigits: 2 })}`;
const date = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
});
/**
 * A short, human reading of an answer: for overviews, history and chat confirmation cards. Malaysian
 * conventions (RM, day month year).
 */
export function describeAnswer(q, value) {
    if (value === null || value === undefined)
        return '—';
    const label = (v) => q.options.find((o) => o.value === v)?.label ?? String(v);
    switch (q.type) {
        case 'single_choice':
        case 'boolean':
            return label(value);
        case 'multiple_choice':
        case 'asset_selector':
            return value.map(label).join(', ');
        case 'entity_selector':
            return Array.isArray(value) ? value.map(label).join(', ') : label(value);
        case 'currency': {
            const c = value;
            return money(c.amount, c.currency);
        }
        case 'percentage':
            return `${value}%`;
        case 'numeric':
            return `${value.toLocaleString('en-MY')}${q.settings.unit ? ` ${q.settings.unit}` : ''}`;
        case 'employee_count':
            return `${value.toLocaleString('en-MY')} people`;
        case 'rating':
            return `${value} of ${q.settings.scale}`;
        case 'date':
            return date(value);
        case 'date_range': {
            const r = value;
            return `${date(r.from)} – ${date(r.to)}`;
        }
        case 'location': {
            const l = value;
            return [l.address, l.city, l.state, l.country].filter(Boolean).join(', ');
        }
        case 'matrix':
            return `${Object.keys(value).length} of ${q.settings.rows.length} rows answered`;
        case 'table':
        case 'repeating_group': {
            const n = value.length;
            return `${n} ${n === 1 ? 'row' : 'rows'}`;
        }
        case 'file_upload':
        case 'image': {
            const n = value.document_ids.length;
            return `${n} ${n === 1 ? 'file' : 'files'}`;
        }
        default:
            return typeof value === 'string' ? value : JSON.stringify(value);
    }
}
//# sourceMappingURL=answers.js.map